/**
 * `docs/DESIGN.md`: "In-app install button driven by `beforeinstallprompt`,
 * plus an iOS card explaining Add to Home Screen, because iOS has no prompt."
 *
 * `classifyInstallAvailability` is the pure decision function — given what
 * the browser has told us, which of the three states applies — and is what
 * the tests exercise directly. `InstallPromptController` is the thin
 * listener wiring around it.
 */

/** Not in TypeScript's DOM lib: `beforeinstallprompt` is a Chromium-only, non-standard event. */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export type InstallAvailability = 'installable' | 'ios-manual' | 'unavailable';

export interface ClassifyInstallInput {
  /** True when the page is already running installed (standalone display mode). */
  readonly isStandalone: boolean;
  /** True once a `beforeinstallprompt` event has fired and not yet been consumed. */
  readonly hasDeferredPrompt: boolean;
  readonly userAgent: string;
}

/**
 * iPadOS 13+ reports as "Macintosh" in its UA string, distinguishable from a
 * real Mac only by touch support — the standard sniff for this.
 */
function looksLikeIOS(userAgent: string, maxTouchPoints: number): boolean {
  if (/iphone|ipad|ipod/i.test(userAgent)) return true;
  return userAgent.includes('Macintosh') && maxTouchPoints > 1;
}

export function classifyInstallAvailability(
  input: ClassifyInstallInput,
  maxTouchPoints = 0,
): InstallAvailability {
  if (input.isStandalone) return 'unavailable';
  if (input.hasDeferredPrompt) return 'installable';
  return looksLikeIOS(input.userAgent, maxTouchPoints) ? 'ios-manual' : 'unavailable';
}

export interface InstallPromptControllerOptions {
  readonly target?: EventTarget;
  readonly navigatorRef?: Navigator;
  readonly matchMediaRef?: (query: string) => MediaQueryList;
  readonly onChange?: (state: InstallAvailability) => void;
}

export class InstallPromptController {
  private deferred: BeforeInstallPromptEvent | null = null;
  private readonly target: EventTarget;
  private readonly navigatorRef: Navigator;
  private readonly matchMediaRef: (query: string) => MediaQueryList;
  private readonly onChange: ((state: InstallAvailability) => void) | undefined;

  private readonly onBeforeInstallPrompt = (event: Event): void => {
    event.preventDefault();
    this.deferred = event as BeforeInstallPromptEvent;
    this.onChange?.(this.state);
  };

  private readonly onAppInstalled = (): void => {
    this.deferred = null;
    this.onChange?.(this.state);
  };

  constructor(options: InstallPromptControllerOptions = {}) {
    this.target = options.target ?? window;
    this.navigatorRef = options.navigatorRef ?? navigator;
    this.matchMediaRef = options.matchMediaRef ?? ((query: string) => window.matchMedia(query));
    this.onChange = options.onChange;

    this.target.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    this.target.addEventListener('appinstalled', this.onAppInstalled);
  }

  get state(): InstallAvailability {
    return classifyInstallAvailability(
      {
        isStandalone: this.matchMediaRef('(display-mode: standalone)').matches,
        hasDeferredPrompt: this.deferred !== null,
        userAgent: this.navigatorRef.userAgent,
      },
      this.navigatorRef.maxTouchPoints,
    );
  }

  /** Show the browser's own install prompt. Only meaningful when `state === 'installable'`. */
  async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const event = this.deferred;
    if (event === null) return 'unavailable';
    this.deferred = null;
    await event.prompt();
    const choice = await event.userChoice;
    this.onChange?.(this.state);
    return choice.outcome;
  }

  destroy(): void {
    this.target.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    this.target.removeEventListener('appinstalled', this.onAppInstalled);
  }
}
